import { Button, Modal, Select } from "components"
import moment from "moment"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { IoAdd, IoEllipsisHorizontal, IoTrashOutline } from "react-icons/io5"
import { nextWeek, today, useApi, useHandleAuth, useToggle } from "utils"
import { useDepartement } from "pages/Card/Transaction/CreateNewEmployee/components/FormSection/utils/hooks/useDepartement"
import { useProjectList } from "pages/Finance/Transaction/GeneralEntry/utils/hooks/useProjectList"
import { useLocationList } from "pages/Finance/Transaction/GeneralEntry/utils/hooks/useLocationList"
import { useDropdownEmployee } from "pages/Sales/Transaction/CustomerOrder/utils/hooks/useDropdownEmployee"
import { useDropdownGroupTask } from "pages/Setup/Preference/SubGroupTask/utils/hooks/useDropdownGroupTask"
import { getPathOnly } from "../utils/functions"
import { useTaskActionList, useTaskCode, useTaskProgress } from "../utils/hooks"
import { useSubGroupList } from "../utils/hooks/useSubGroupTaskList"
import { TaskActionList, TaskList } from "../types"
import { ACTION_STATUS, TASK_PRIORITY, TASK_STATUS } from "../utils/vars"
import { ModalActionLine } from "./ModalActionLine"

function formatTaskApiError(err: unknown): string {
  const e = err as {
    response?: {
      data?: {
        message?: string
        detail?:
          | string
          | { message?: string }
          | Array<{ msg?: string; message?: string }>
      }
    }
  }
  const d = e?.response?.data?.detail
  if (typeof d === "string") return d
  if (Array.isArray(d) && d.length) {
    const first = d[0]
    if (first && typeof first === "object") {
      return String(first.msg ?? first.message ?? JSON.stringify(first))
    }
  }
  if (d && typeof d === "object" && "message" in (d as object)) {
    return String((d as { message?: string }).message)
  }
  const msg = e?.response?.data?.message
  if (typeof msg === "string") return msg
  return "Request failed"
}

type ModalTaskDetailProps = {
  data: TaskList
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export function ModalTaskDetail({
  data,
  isOpen,
  onClose,
  onSuccess
}: ModalTaskDetailProps) {
  const api = useApi()
  const { userTokenData } = useHandleAuth()
  const userToken = userTokenData()
  const isCreateMode = !data.task_id
  const [trxCode, setTrxCode] = useState("")
  const [subject, setSubject] = useState("")
  const [description, setDescription] = useState("")
  const [locationId, setLocationId] = useState<number>(0)
  const [departmentId, setDepartmentId] = useState<number>(0)
  const [projectId, setProjectId] = useState<number>(0)
  const [groupTaskId, setGroupTaskId] = useState<number>(0)
  const [subGroupId, setSubGroupId] = useState<number>(0)
  const [ownerTaskId, setOwnerTaskId] = useState<number>(0)
  const [taskStatus, setTaskStatus] = useState<number>(1)
  const [priority, setPriority] = useState<number>(1)
  const [startDate, setStartDate] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [editingActionId, setEditingActionId] = useState<number | null>(null)
  const [updatingActionId, setUpdatingActionId] = useState<number | null>(null)
  const [openStatusMenuId, setOpenStatusMenuId] = useState<number | null>(null)
  const [deletingAction, setDeletingAction] = useState<TaskActionList | null>(null)
  const [isDeletingAction, setIsDeletingAction] = useState(false)
  const [confirmDeleteTaskOpen, setConfirmDeleteTaskOpen] = useState(false)
  const [isDeletingTask, setIsDeletingTask] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const modalActionLine = useToggle(false)
  const taskId = isCreateMode ? "" : data.task_id?.toString() ?? ""
  const taskAction = useTaskActionList({ task_id: taskId })
  const taskProgress = useTaskProgress({ task_id: taskId })
  const locations = useLocationList()
  const departments = useDepartement()
  const projects = useProjectList()
  const groupTasks = useDropdownGroupTask()
  const subGroups = useSubGroupList()
  const employees = useDropdownEmployee()
  useTaskCode({
    key: startDate,
    trx_date: startDate || today(),
    enabled: isCreateMode,
    onSuccess: (code) => setTrxCode(code)
  })

  useEffect(() => {
    if (!isOpen) return
    setTrxCode(data.trx_code ?? "")
    setSubject(data.subject ?? "")
    setDescription(data.description ?? "")
    setLocationId(data.location_id ?? 0)
    setDepartmentId(data.departement_id ?? 0)
    setProjectId(data.project_id ?? 0)
    setGroupTaskId(data.mr_group_task_id ?? 0)
    setSubGroupId(data.mr_group_sub_id ?? 0)
    setOwnerTaskId(data.owner_task_id ?? userToken?.user_id ?? 0)
    setTaskStatus(data.task_status ?? 1)
    setPriority(data.priority ?? 1)
    setStartDate(
      data.date_start ? moment(data.date_start).format("YYYY-MM-DD") : today()
    )
    setDueDate(
      data.date_end ? moment(data.date_end).format("YYYY-MM-DD") : nextWeek()
    )
    setEditingActionId(null)
    setUpdatingActionId(null)
    setOpenStatusMenuId(null)
  }, [data, isOpen, userToken?.user_id])

  useEffect(() => {
    if (!isOpen) {
      setDeletingAction(null)
      setConfirmDeleteTaskOpen(false)
    }
  }, [isOpen])

  const statusOptions = useMemo(
    () =>
      Object.values(TASK_STATUS)
        .filter((v) => typeof v === "number")
        .map((v) => ({
          value: String(Number(v)),
          label: TASK_STATUS[v as number] as string
        })),
    []
  )

  const priorityOptions = useMemo(
    () =>
      Object.values(TASK_PRIORITY)
        .filter((v) => typeof v === "number")
        .map((v) => ({
          value: String(Number(v)),
          label: TASK_PRIORITY[v as number] as string
        })),
    []
  )
  const subGroupOptions = useMemo(
    () =>
      subGroups.dropdown.filter(
        (item) => Number(item.group_task_id) === Number(groupTaskId)
      ),
    [subGroups.dropdown, groupTaskId]
  )

  const getActionStatusLabel = (status: number) =>
    ACTION_STATUS[status] ? String(ACTION_STATUS[status]) : "UNKNOWN"

  const getActionStatusClass = (status: number) => {
    switch (status) {
      case 1:
        return "bg-slate-100 text-slate-700 border-slate-200"
      case 2:
        return "bg-blue-100 text-blue-700 border-blue-200"
      case 3:
        return "bg-emerald-100 text-emerald-700 border-emerald-200"
      case 4:
        return "bg-rose-100 text-rose-700 border-rose-200"
      default:
        return "bg-base-200 text-base-content border-base-300"
    }
  }

  const progressPercent = useMemo(() => {
    if (isCreateMode) return 0
    const total = taskAction.data.length
    if (total === 0) return 0
    const done = taskAction.data.filter((item) => item.status === 3).length
    return Math.round((done / total) * 100)
  }, [taskAction.data, isCreateMode])

  const handleOpenActionLine = (id: number | null) => {
    setEditingActionId(id)
    modalActionLine.setActive(true)
  }

  const handleUpdateActionStatus = async (
    action: TaskActionList,
    newStatus: number
  ) => {
    if (newStatus === action.status) return

    const imgPath = (action.task_action_files ?? [])
      .map((file) => ({
        name: file.file_name,
        path: getPathOnly(file.path) ?? ""
      }))
      .filter((file) => file.path)

    const payload = {
      task_id: action.task_id,
      trx_code: action.trx_code,
      date_start: action.date_start,
      date_end: action.date_end,
      action_task_id: action.action_task_id,
      assign_to_id: action.assign_to_id,
      description: action.description || "-",
      status: newStatus,
      imgPath
    }

    setUpdatingActionId(action.task_action_id)
    await toast
      .promise(api.put(`/task_action/${action.task_action_id}`, payload), {
        loading: "Updating action...",
        success: "Action updated",
        error: (err) =>
          err?.response?.data?.detail?.message ??
          err?.response?.data?.message ??
          "Failed to update action"
      })
      .then(() => {
        taskAction.refetch()
        taskProgress.refetch()
      })
      .finally(() => setUpdatingActionId(null))
  }

  const handleDeleteAction = async (action: TaskActionList) => {
    setDeletingAction(action)
  }

  const handleConfirmDeleteAction = async () => {
    if (!deletingAction) return
    const id = deletingAction.task_action_id
    setIsDeletingAction(true)

    /** BE: DELETE bisa 409 (FK). Fallback soft-delete sama seperti pola deactivate. */
    const runDeleteOrDeactivate = async () => {
      try {
        return await api.delete(`/task_action/${id}`)
      } catch (firstErr: unknown) {
        const status = (firstErr as { response?: { status?: number } })?.response
          ?.status
        if (status === 409) {
          return await api.patch(`/task_action/${id}/is_deleted`, {
            is_deleted: true
          })
        }
        throw firstErr
      }
    }

    await toast
      .promise(runDeleteOrDeactivate(), {
        loading: "Deleting action...",
        success: (res) =>
          (res as { data?: { message?: string } })?.data?.message ??
          "Action removed",
        error: (err) => formatTaskApiError(err)
      })
      .then(() => {
        taskAction.refetch()
        taskProgress.refetch()
        setDeletingAction(null)
      })
      .finally(() => setIsDeletingAction(false))
  }

  const handleConfirmDeleteTask = async () => {
    const id = data.task_id
    if (!id) return
    setIsDeletingTask(true)

    const runDeleteOrDeactivate = async () => {
      try {
        return await api.delete(`/task/${id}`)
      } catch (firstErr: unknown) {
        const status = (firstErr as { response?: { status?: number } })?.response
          ?.status
        if (status === 409) {
          return await api.patch(`/task/${id}/is_deleted`, { is_deleted: true })
        }
        throw firstErr
      }
    }

    await toast
      .promise(runDeleteOrDeactivate(), {
        loading: "Deleting task...",
        success: (res) =>
          (res as { data?: { message?: string } })?.data?.message ??
          "Task removed",
        error: (err) => formatTaskApiError(err)
      })
      .then(() => {
        setConfirmDeleteTaskOpen(false)
        onSuccess?.()
        onClose()
      })
      .finally(() => setIsDeletingTask(false))
  }

  const handleSave = async () => {
    if (
      !trxCode ||
      !locationId ||
      !departmentId ||
      !projectId ||
      !groupTaskId ||
      !subGroupId ||
      !ownerTaskId
    ) {
      toast.error("Please complete required IDs before saving.")
      return
    }

    const payload = {
      trx_code: trxCode,
      location_id: locationId,
      departement_id: departmentId,
      project_id: projectId,
      mr_group_task_id: groupTaskId,
      mr_group_sub_id: subGroupId,
      task_status: taskStatus,
      subject: subject.trim() || "-",
      priority,
      date_start: startDate || data.date_start,
      date_end: dueDate || data.date_end,
      owner_task_id: ownerTaskId,
      description: description.trim() || "-"
    }
    const savePromise = isCreateMode
      ? api.post("/task", payload)
      : api.put(`/task/${data.task_id}`, payload)

    setIsSaving(true)
    await toast
      .promise(savePromise, {
        loading: isCreateMode ? "Creating task..." : "Saving task...",
        success: (res) =>
          res?.data?.message ?? (isCreateMode ? "Task created" : "Task updated"),
        error: (err) =>
          err?.response?.data?.detail?.message ??
          err?.response?.data?.message ??
          (isCreateMode ? "Failed to create task" : "Failed to update task")
      })
      .then(() => {
        onSuccess?.()
        onClose()
      })
      .finally(() => setIsSaving(false))
  }

  return (
    <Modal
      isOpen={isOpen}
      title={isCreateMode ? "CREATE NEW TASK" : "TASK DETAIL"}
      size="xl"
      isdarktitle
      closeModal={onClose}>
      <Modal.Body className="h-auto !px-4 !py-4 md:!px-5 md:!py-5">
        <section className="grid grid-cols-1 gap-4 md:grid-cols-[1.3fr_0.7fr]">
          <div className="space-y-3">
            <div className="rounded-xl border border-base-300 bg-base-100 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-black uppercase tracking-wide text-base-content">
                  Completion
                </span>
                <span className="text-3xl font-black leading-none text-base-content">
                  {progressPercent}%
                </span>
              </div>
              <div className="h-3.5 w-full overflow-hidden rounded-full bg-base-300">
                <div
                  className={`h-full rounded-full transition-all ${
                    progressPercent >= 80
                      ? "bg-emerald-500"
                      : progressPercent >= 45
                      ? "bg-blue-500"
                      : "bg-amber-500"
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, progressPercent))}%` }}
                />
              </div>
            </div>

            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Task ID
              </label>
              <input
                disabled={!isCreateMode}
                value={trxCode}
                onChange={(e) => setTrxCode(e.target.value)}
                className="w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2.5 text-base font-semibold text-base-content"
              />
            </div>

            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Subject
              </label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2.5 text-base font-semibold text-base-content"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="field">
                <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2.5 text-base text-base-content"
                />
              </div>
              <div className="field">
                <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2.5 text-base text-base-content"
                />
              </div>
            </div>

            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[96px] w-full rounded-lg border border-base-300 bg-base-100 px-3 py-2.5 text-base text-base-content"
              />
            </div>

            {!isCreateMode ? (
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Task Action
              </label>
              <div className="rounded-lg border border-base-300 bg-base-200/30 p-3 text-base text-base-content/60">
                <div className="mb-2 flex justify-end">
                  <button
                    type="button"
                    title="New action line"
                    onClick={() => handleOpenActionLine(null)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-base-300 bg-base-100 text-base-content hover:bg-base-200">
                    <IoAdd className="size-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  {taskAction.isLoading ? (
                    <div className="space-y-2">
                      {[1, 2].map((s) => (
                        <div
                          key={s}
                          className="rounded-md border border-base-300 bg-base-100 px-3 py-2 animate-pulse">
                          <div className="h-3 w-32 rounded bg-base-300 mb-2" />
                          <div className="h-4 w-2/3 rounded bg-base-300 mb-2" />
                          <div className="h-3 w-full rounded bg-base-300" />
                        </div>
                      ))}
                      <p className="text-xs font-semibold text-base-content/50 px-1">
                        Loading action lines...
                      </p>
                    </div>
                  ) : !taskAction.data.length ? (
                    <p className="text-sm text-base-content/50">
                      No action line yet
                    </p>
                  ) : (
                    taskAction.data.map((item) => (
                      <div
                        key={item.task_action_id}
                        className="rounded-md border border-base-300 bg-base-100 px-3 py-2">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold uppercase text-base-content/60">
                                {item.trx_code}
                              </span>
                              <span className="text-xs text-base-content/50">
                                #{item.task_action_id}
                              </span>
                            </div>
                            <p className="mt-1 text-base font-semibold text-base-content truncate">
                              {item.mr_action_task_name}
                            </p>
                            <p className="mt-1 line-clamp-2 text-sm text-base-content/70">
                              {item.description || "-"}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-base-content/55">
                              <span>Assign: {item.assign_to_name || "-"}</span>
                              <span>
                                Date: {moment(item.date_start).format("YYYY-MM-DD")} -{" "}
                                {moment(item.date_end).format("YYYY-MM-DD")}
                              </span>
                            </div>
                          </div>

                          <div className="relative flex items-center gap-2">
                            <button
                              type="button"
                              title="Change status"
                              disabled={updatingActionId === item.task_action_id}
                              onClick={() =>
                                setOpenStatusMenuId((prev) =>
                                  prev === item.task_action_id
                                    ? null
                                    : item.task_action_id
                                )
                              }
                              className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                                getActionStatusClass(item.status)
                              }`}>
                              {updatingActionId === item.task_action_id
                                ? "..."
                                : getActionStatusLabel(item.status)}
                            </button>
                            {openStatusMenuId === item.task_action_id ? (
                              <div className="absolute right-12 top-10 z-20 min-w-[170px] rounded-lg border border-base-300 bg-base-100 p-1 shadow-lg">
                                {[1, 2, 3, 4].map((status) => (
                                  <button
                                    key={status}
                                    type="button"
                                    onClick={() => {
                                      setOpenStatusMenuId(null)
                                      handleUpdateActionStatus(item, status)
                                    }}
                                    className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-base-200">
                                    <span className="text-xs font-semibold text-base-content">
                                      {getActionStatusLabel(status)}
                                    </span>
                                    <span
                                      className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${getActionStatusClass(
                                        status
                                      )}`}>
                                      {status}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            ) : null}

                            <button
                              type="button"
                              title="Open action detail"
                              onClick={() => handleOpenActionLine(item.task_action_id)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-base-300 text-base-content/70 hover:bg-base-200">
                              <IoEllipsisHorizontal className="size-4" />
                            </button>
                            <button
                              type="button"
                              title="Delete action"
                              onClick={() => handleDeleteAction(item)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50">
                              <IoTrashOutline className="size-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Status
              </label>
              <Select
                options={statusOptions}
                value={statusOptions.find((s) => Number(s.value) === taskStatus) ?? null}
                onChange={(e) => {
                  if (e?.value) setTaskStatus(Number(e.value))
                }}
                placeholder="Select status"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Priority
              </label>
              <Select
                options={priorityOptions}
                value={priorityOptions.find((s) => Number(s.value) === priority) ?? null}
                onChange={(e) => {
                  if (e?.value) setPriority(Number(e.value))
                }}
                placeholder="Select priority"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Location
              </label>
              <Select
                options={locations.dropdown ?? []}
                isLoading={locations.isLoading}
                value={
                  locations.dropdown.find((o) => Number(o.value) === locationId) ??
                  null
                }
                onChange={(e) => {
                  if (e?.value) setLocationId(Number(e.value))
                }}
                placeholder="Select location"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Department
              </label>
              <Select
                options={departments.dataDepartement ?? []}
                isLoading={departments.isLoadingDepartement}
                value={
                  departments.dataDepartement.find(
                    (o) => Number(o.value) === departmentId
                  ) ?? null
                }
                onChange={(e) => {
                  if (e?.value) setDepartmentId(Number(e.value))
                }}
                placeholder="Select department"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Project
              </label>
              <Select
                options={projects.dropdown ?? []}
                isLoading={projects.isLoading}
                value={
                  projects.dropdown.find((o) => Number(o.value) === projectId) ??
                  null
                }
                onChange={(e) => {
                  if (e?.value) setProjectId(Number(e.value))
                }}
                placeholder="Select project"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Group
              </label>
              <Select
                options={groupTasks.data ?? []}
                isLoading={groupTasks.isLoading}
                value={
                  groupTasks.data.find((o) => Number(o.value) === groupTaskId) ??
                  null
                }
                onChange={(e) => {
                  const selected = Number(e?.value) || 0
                  setGroupTaskId(selected)
                  setSubGroupId(0)
                }}
                placeholder="Select group"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Sub Group
              </label>
              <Select
                options={subGroupOptions ?? []}
                isLoading={subGroups.isLoading}
                value={
                  subGroupOptions.find((o) => Number(o.value) === subGroupId) ??
                  null
                }
                onChange={(e) => {
                  if (e?.value) setSubGroupId(Number(e.value))
                }}
                placeholder="Select sub group"
              />
            </div>
            <div className="field">
              <label className="mb-1 block text-xs uppercase font-black tracking-wide text-base-content">
                Owner
              </label>
              <Select
                options={employees.dropdown ?? []}
                isLoading={employees.isLoading}
                value={
                  employees.dropdown.find((o) => Number(o.value) === ownerTaskId) ??
                  null
                }
                onChange={(e) => {
                  if (e?.value) setOwnerTaskId(Number(e.value))
                }}
                placeholder="Select owner"
              />
            </div>
          </div>
        </section>
      </Modal.Body>

      <Modal.Footer className="flex items-center justify-end gap-2">
        {!isCreateMode ? (
          <Button
            color="outline-error"
            type="button"
            disabled={isSaving || isDeletingTask}
            onClick={() => setConfirmDeleteTaskOpen(true)}>
            Delete
          </Button>
        ) : null}
        <Button
          color="primary"
          type="button"
          loading={isSaving ? "true" : undefined}
          disabled={isSaving}
          onClick={handleSave}>
          {isCreateMode ? "Create Task" : "Save Task"}
        </Button>
      </Modal.Footer>

      {modalActionLine.isActive && (
        <ModalActionLine
          id={editingActionId}
          task={data}
          modalState={modalActionLine}
          taskAction={taskAction}
          taskProgress={taskProgress}
        />
      )}

      <Modal
        isOpen={confirmDeleteTaskOpen}
        closeModal={() => {
          if (!isDeletingTask) setConfirmDeleteTaskOpen(false)
        }}
        title="CONFIRM DELETE TASK"
        size="md"
        style={{ maxWidth: "460px" }}
        isdarktitle
        zIndexClassName="relative z-[100]">
        <Modal.Body className="!h-auto">
          <p className="text-sm text-base-content/80">
            Are you sure you want to delete this task?
          </p>
          <div className="mt-3 rounded-lg border border-base-300 bg-base-100 p-3 text-sm space-y-1">
            <p>
              <span className="font-bold text-base-content">Task ID:</span>{" "}
              <span className="text-base-content/80">{data.trx_code || "-"}</span>
            </p>
            <p>
              <span className="font-bold text-base-content">Subject:</span>{" "}
              <span className="text-base-content/80">{data.subject || "-"}</span>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex items-center justify-end gap-2">
          <Button
            type="button"
            color="ghost"
            disabled={isDeletingTask}
            onClick={() => setConfirmDeleteTaskOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            color="error"
            loading={isDeletingTask ? "true" : undefined}
            disabled={isDeletingTask}
            onClick={handleConfirmDeleteTask}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={Boolean(deletingAction)}
        closeModal={() => {
          if (!isDeletingAction) setDeletingAction(null)
        }}
        title="CONFIRM DELETE ACTION"
        size="md"
        style={{ maxWidth: "460px" }}
        isdarktitle
        zIndexClassName="relative z-[100]">
        <Modal.Body className="!h-auto">
          <p className="text-sm text-base-content/80">
            Are you sure you want to delete this action line?
          </p>
          <div className="mt-3 rounded-lg border border-base-300 bg-base-100 p-3 text-sm space-y-1">
            <p>
              <span className="font-bold text-base-content">Task ID:</span>{" "}
              <span className="text-base-content/80">{data.trx_code || "-"}</span>
            </p>
            <p>
              <span className="font-bold text-base-content">Action ID:</span>{" "}
              <span className="text-base-content/80">
                {deletingAction ? `#${deletingAction.task_action_id}` : "-"}
              </span>
            </p>
            <p>
              <span className="font-bold text-base-content">Action Code:</span>{" "}
              <span className="text-base-content/80">
                {deletingAction?.trx_code || "-"}
              </span>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex items-center justify-end gap-2">
          <Button
            type="button"
            color="ghost"
            disabled={isDeletingAction}
            onClick={() => setDeletingAction(null)}>
            Cancel
          </Button>
          <Button
            type="button"
            color="error"
            loading={isDeletingAction ? "true" : undefined}
            disabled={isDeletingAction}
            onClick={handleConfirmDeleteAction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  )
}
